// All comments are in English.

import React from "react";
import type { DateRangeQuery } from "../api/overviewApi";
import { ShoppingBasket } from "lucide-react";
import { formatHKD } from "./utils";

/**
 * Minimal shape for customer product summary response.
 */
export interface CustomerProductSummary {
  total_gmv: number;
  product_count: number;
  top_product_name: string | null;
  top_product_gmv: number | null;
  top_product_share: number | null;
}

/** Helper to format percentage values (0-1) into xx%. */
const formatPercent = (value: number | null | undefined): string => {
  if (value == null) return "-";
  return `${(value * 100).toFixed(1)}%`;
};

interface CustomerProductSummaryCardProps {
  summary?: CustomerProductSummary | null;
  totalGmvInRange: number;
  productCountInRange: number;
  dateRange: DateRangeQuery;
}

/**
 * Left-side KPI card: numeric purchase summary for the customer.
 */
export const CustomerProductSummaryCard: React.FC<
  CustomerProductSummaryCardProps
> = ({ summary, totalGmvInRange, productCountInRange, dateRange }) => {
  return (
    <section className="rounded-lg border bg-card p-4 shadow-sm">
      <div className="mb-1 flex items-center gap-2">
        <ShoppingBasket className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold">Purchase summary</h2>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Total GMV (all time)</p>
          <p className="text-base font-semibold">
            {summary ? formatHKD(summary.total_gmv) : "-"}
          </p>
          <p className="text-[11px] text-muted-foreground">
            Across {summary?.product_count ?? 0} products
          </p>
        </div>

        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Top product</p>
          <p className="text-sm font-semibold">
            {summary?.top_product_name ?? "-"}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {summary?.top_product_gmv != null ? (
              <>
                {formatHKD(summary.top_product_gmv)} (
                {formatPercent(summary.top_product_share)} of total)
              </>
            ) : (
              "No single dominant product"
            )}
          </p>
        </div>

        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">
            GMV in selected period
          </p>
          <p className="text-base font-semibold">
            {formatHKD(totalGmvInRange)}
          </p>
          <p className="text-[11px] text-muted-foreground">
            From {productCountInRange} products in range
          </p>
        </div>

        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Date window</p>
          <p className="text-[11px] font-medium">
            {dateRange.date_from} – {dateRange.date_to}
          </p>
        </div>
      </div>
    </section>
  );
};

/**
 * Explanation card moved from CustomerProductInsights.tsx
 * How to read: Purchase summary
 */
export const CustomerProductSummaryExplanation: React.FC = () => {
  return (
    <section
      className="rounded-lg border bg-muted/30 p-4 text-xs leading-relaxed shadow-sm"
      aria-label="How to read purchase summary"
    >
      <h2 className="mb-2 text-sm font-semibold">
        How to read: Purchase summary
      </h2>

      <div className="space-y-3 text-[11px] text-muted-foreground">
        <div>
          <h3 className="text-xs font-semibold">1. Total GMV (all time)</h3>
          <p className="mt-1">
            This shows the cumulative gross merchandise value (GMV) this
            customer has generated since the beginning of your data.
          </p>
        </div>

        <div>
          <h3 className="text-xs font-semibold">2. Top product</h3>
          <p className="mt-1">
            Displays the customer's highest-GMV product. A high share indicates
            heavy concentration on one SKU.
          </p>
        </div>

        <div>
          <h3 className="text-xs font-semibold">3. GMV in selected period</h3>
          <p className="mt-1">
            Shows how active the customer is in the current date range.
          </p>
        </div>

        <div>
          <h3 className="text-xs font-semibold">4. Date window</h3>
          <p className="mt-1">
            Indicates the active period used for filtering the metrics.
          </p>
        </div>
      </div>
    </section>
  );
};
